import { createMemo, createSignal, For } from 'solid-js'
import {
  commit,
  moveInOrder,
  scene,
  select,
  selection,
  toggleSelection,
  updateNode,
} from '../scene/store.ts'
import type { NodeType, SceneNode } from '../scene/types.ts'
import { Icon } from './Icon.tsx'

const TYPE_LABELS: Record<NodeType, string> = {
  image: 'Image',
  rect: 'Rectangle',
  ellipse: 'Ellipse',
  line: 'Line',
  path: 'Drawing',
  text: 'Text',
}

function label(node: SceneNode): string {
  if (node.name) return node.name
  if (node.type === 'text') {
    const firstLine = node.text.split('\n')[0]?.trim() ?? ''
    if (firstLine) return firstLine.length > 28 ? `${firstLine.slice(0, 28)}…` : firstLine
  }
  return TYPE_LABELS[node.type]
}

interface Row {
  id: string
  index: number
  node: SceneNode
}

type Item = { kind: 'node'; row: Row; member: boolean } | { kind: 'group'; key: string; ids: string[] }

export function LayersPanel() {
  // Topmost first, which is how the canvas reads.
  const rows = createMemo(() =>
    scene.order
      .map((id, index) => ({ id, index, node: scene.nodes[id] }))
      .filter((r): r is Row => !!r.node)
      .reverse(),
  )

  /** Rows with a header in front of each consecutive run of one group's members. */
  const items = createMemo<Item[]>(() => {
    const out: Item[] = []
    let header: { ids: string[] } | null = null
    let prevGroup: string | undefined
    for (const row of rows()) {
      const g = row.node.groupId
      if (!g) header = null
      else if (g !== prevGroup) {
        header = { ids: [] }
        out.push({ kind: 'group', key: `${g}:${row.id}`, ids: header.ids })
      }
      header?.ids.push(row.id)
      out.push({ kind: 'node', row, member: !!g })
      prevGroup = g
    }
    return out
  })

  const selectGroup = (ids: string[], e: MouseEvent) => {
    const sel = selection()
    if (!e.shiftKey) select(ids)
    else if (ids.every((id) => sel.includes(id))) select(sel.filter((id) => !ids.includes(id)))
    else select([...new Set([...sel, ...ids])])
  }

  const [dragId, setDragId] = createSignal<string | null>(null)
  /** Row the dragged layer would land next to, and on which side of it in the list. */
  const [dropAt, setDropAt] = createSignal<{ id: string; above: boolean } | null>(null)

  const endDrag = () => {
    setDragId(null)
    setDropAt(null)
  }

  const toggleHidden = (node: SceneNode) => {
    commit(node.hidden ? 'Show' : 'Hide', [node.id], () =>
      updateNode(node.id, { hidden: !node.hidden }),
    )
  }

  const toggleLocked = (node: SceneNode) => {
    commit(node.locked ? 'Unlock' : 'Lock', [node.id], () =>
      updateNode(node.id, { locked: !node.locked }),
    )
    if (!node.locked) select(selection().filter((id) => id !== node.id))
  }

  /**
   * The list runs topmost first, so landing above a row in the list means just
   * after it in paint order, and below means just before it.
   */
  const drop = () => {
    const id = dragId()
    const at = dropAt()
    endDrag()
    if (!id || !at || at.id === id) return
    const rest = scene.order.filter((x) => x !== id)
    const target = rest.indexOf(at.id)
    if (target === -1) return
    moveInOrder(id, at.above ? target + 1 : target)
  }

  return (
    <div class="panel layers">
      <div class="panel-title">Layers</div>

      <div class="layer-list">
        <For each={items()}>
          {(item) => {
            if (item.kind === 'group') {
              return (
                <div
                  class="layer group-header"
                  classList={{ selected: item.ids.every((id) => selection().includes(id)) }}
                  onClick={(e) => selectGroup(item.ids, e)}
                >
                  <span class="layer-icon">
                    <Icon name="folder" size={15} />
                  </span>
                  <span class="layer-name">Group</span>
                </div>
              )
            }
            const row = item.row
            return (
            <div
              class="layer"
              classList={{
                selected: selection().includes(row.id),
                dragging: dragId() === row.id,
                member: item.member,
                'drop-above': dropAt()?.id === row.id && dropAt()!.above,
                'drop-below': dropAt()?.id === row.id && !dropAt()!.above,
              }}
              draggable={true}
              onDragStart={() => setDragId(row.id)}
              onDragEnd={endDrag}
              onDragOver={(e) => {
                e.preventDefault()
                if (!dragId() || dragId() === row.id) {
                  setDropAt(null)
                  return
                }
                const r = e.currentTarget.getBoundingClientRect()
                const above = e.clientY < r.top + r.height / 2
                const cur = dropAt()
                if (cur?.id !== row.id || cur.above !== above) setDropAt({ id: row.id, above })
              }}
              onDrop={(e) => {
                e.preventDefault()
                e.stopPropagation()
                drop()
              }}
              onClick={(e) => (e.shiftKey ? toggleSelection(row.id) : select([row.id]))}
            >
              <button
                type="button"
                class="layer-icon"
                title={row.node.hidden ? 'Show' : 'Hide'}
                onClick={(e) => {
                  e.stopPropagation()
                  toggleHidden(row.node)
                }}
              >
                <Icon name={row.node.hidden ? 'eyeOff' : 'eye'} size={15} />
              </button>

              <span class="layer-name">{label(row.node)}</span>

              <button
                type="button"
                class="layer-icon"
                title={row.node.locked ? 'Unlock' : 'Lock'}
                onClick={(e) => {
                  e.stopPropagation()
                  toggleLocked(row.node)
                }}
              >
                <Icon name={row.node.locked ? 'lock' : 'unlock'} size={15} />
              </button>
            </div>
            )
          }}
        </For>

        <p class="muted hint" classList={{ hidden: rows().length > 0 }}>
          Nothing on the canvas yet.
        </p>
      </div>
    </div>
  )
}
